/**
 * Curated map of well-known third-party script hosts/paths → consent category.
 * Used by the generic auto-block (auto-block-known.ts) to neutralize recognized
 * trackers by their `src` BEFORE they run, without the site owner tagging them.
 *
 * Deliberately conservative:
 *   - We match concrete, unambiguous tracker endpoints, not broad domains.
 *   - The Google Tag Manager *container* (gtm.js) is intentionally NOT listed:
 *     GTM should keep running so it can drive Google Consent Mode v2; blocking
 *     it would break that integration. Direct vendor scripts (GA4 gtag, Meta,
 *     etc.) ARE listed.
 *
 * Extend freely — order doesn't matter, first match wins.
 */

import type { ConsentChoices } from "./types.js";

type VendorCategory = keyof ConsentChoices;

interface Vendor {
  /** Human label (debugging / future UI). */
  name: string;
  /** Matched against the full script src URL. */
  pattern: RegExp;
  category: VendorCategory;
}

const VENDORS: Vendor[] = [
  // --- Analytics ---------------------------------------------------------
  { name: "Google Analytics", pattern: /(^|\.)google-analytics\.com\//i, category: "analytics" },
  {
    name: "Google Analytics 4 (gtag)",
    pattern: /googletagmanager\.com\/gtag\/js/i,
    category: "analytics",
  },
  { name: "Hotjar", pattern: /(static|script)\.hotjar\.com\//i, category: "analytics" },
  { name: "Microsoft Clarity", pattern: /(^|\.)clarity\.ms\//i, category: "analytics" },
  { name: "Mixpanel", pattern: /cdn\.mxpnl\.com\//i, category: "analytics" },
  { name: "Matomo", pattern: /\/(matomo|piwik)\.js/i, category: "analytics" },
  { name: "Amplitude", pattern: /cdn\.amplitude\.com\//i, category: "analytics" },
  { name: "Segment", pattern: /cdn\.segment\.(com|io)\//i, category: "analytics" },
  // AI-referral session tracking (ChatGPT, Claude, Gemini, Perplexity…); sets
  // _vk_* cookies — client case 2026-10 (maisonetfinance.fr).
  { name: "Visibility Kit", pattern: /cdn\.visibilitykit\.ai\//i, category: "analytics" },

  // --- Marketing / advertising ------------------------------------------
  {
    name: "Meta Pixel",
    pattern: /connect\.facebook\.net\/.*\/fbevents\.js/i,
    category: "marketing",
  },
  {
    name: "Google Ads / DoubleClick",
    pattern: /(doubleclick\.net|googleadservices\.com|googlesyndication\.com)\//i,
    category: "marketing",
  },
  { name: "Microsoft Ads (UET)", pattern: /bat\.bing\.com\//i, category: "marketing" },
  {
    name: "LinkedIn Insight",
    pattern: /(snap\.licdn\.com|px\.ads\.linkedin\.com)\//i,
    category: "marketing",
  },
  { name: "TikTok Pixel", pattern: /analytics\.tiktok\.com\//i, category: "marketing" },
  {
    name: "Snapchat Pixel",
    pattern: /(sc-static\.net|tr\.snapchat\.com)\//i,
    category: "marketing",
  },
  {
    name: "X (Twitter) Ads",
    pattern: /(static\.ads-twitter\.com|analytics\.twitter\.com)\//i,
    category: "marketing",
  },
  {
    name: "Pinterest Tag",
    pattern: /(ct\.pinterest\.com|s\.pinimg\.com\/ct)\//i,
    category: "marketing",
  },
  { name: "Criteo", pattern: /(^|\.)criteo\.(net|com)\//i, category: "marketing" },
  { name: "Taboola", pattern: /cdn\.taboola\.com\//i, category: "marketing" },
  { name: "Outbrain", pattern: /widgets\.outbrain\.com\//i, category: "marketing" },

  // --- Social / embeds ---------------------------------------------------
  { name: "Facebook SDK", pattern: /connect\.facebook\.net\/.*\/sdk\.js/i, category: "social" },
  { name: "Twitter widgets", pattern: /platform\.twitter\.com\//i, category: "social" },
  { name: "Instagram embeds", pattern: /platform\.instagram\.com\//i, category: "social" },
  {
    name: "YouTube iframe API",
    pattern: /youtube\.com\/(iframe_api|player_api)/i,
    category: "social",
  },
  { name: "AddThis", pattern: /(s7|cache)\.addthis\.com\//i, category: "social" },

  // --- Personalization / support widgets --------------------------------
  {
    name: "Intercom",
    pattern: /(widget\.intercom\.io|js\.intercomcdn\.com)\//i,
    category: "personalization",
  },
  { name: "Drift", pattern: /js\.driftt\.com\//i, category: "personalization" },
  {
    name: "Zendesk Chat",
    pattern: /(static\.zdassets\.com|v2\.zopim\.com)\//i,
    category: "personalization",
  },
  { name: "Crisp", pattern: /client\.crisp\.chat\//i, category: "personalization" },
];

/** Return the consent category a script src belongs to, or null if unknown. */
export function vendorCategoryForSrc(src: string): VendorCategory | null {
  if (!src) return null;
  for (const v of VENDORS) {
    if (v.pattern.test(src)) return v.category;
  }
  return null;
}
